import { Card, EmptyState, STATUS_TONES } from '@hgc/ui';
import type { VehicleState } from '@hgc/core';
import type { LinkStats } from '@hgc/link';
import type { JSX } from 'react';

import { useLinkStats, useLinkStatus, useNow, useVehicleState } from '../runtime/hooks';
import { buildInstruments, type InstrumentSection } from './model';

/**
 * 仪表面板：电池、GPS、EKF 与振动、链路质量。
 *
 * 与"读数表"的分工：读数表回答"每一项的数值是多少"，仪表回答"**现在有没有问题**"，
 * 所以每一块前面都有一句结论（`summary`），异常时它自带语义色与完整句子 ——
 * 一个淡灰色小点无法承担"电池只剩 8%"这件事。
 *
 * 每一项都同时给出**值**与**这一项能不能信**：`freshness` 区分三种状态
 * （有数据且新鲜 / 有数据但过期 / 根本没有数据），并把它们写在 `note` 里。
 * "剩余电量 `--`"与"剩余电量 0%"在界面上长得完全不一样，这是刻意的。
 */

export interface InstrumentsProps {
  readonly state: VehicleState | null;
  readonly stats: LinkStats | null;
  readonly now: number;
  readonly emptyReason: string;
}

export function Instruments({ state, stats, now, emptyReason }: InstrumentsProps): JSX.Element {
  if (state === null) {
    return (
      <Card title="飞行仪表">
        <EmptyState level="warning" title="没有可显示的仪表数据" description={emptyReason} />
      </Card>
    );
  }

  const sections = buildInstruments(state, stats, now);

  return (
    <div className="grid gap-3 xl:grid-cols-2" data-hgc-instruments="">
      {sections.map((section) => (
        <SectionCard key={section.id} section={section} />
      ))}
    </div>
  );
}

function SectionCard({ section }: { readonly section: InstrumentSection }): JSX.Element {
  const tone = STATUS_TONES[section.level];
  const alarming = section.level === 'warning' || section.level === 'critical';

  return (
    <Card
      title={section.title}
      level={section.level}
      actions={
        <span
          data-hgc-instrument-section-state={section.level}
          className={`rounded-full border px-2 py-0 text-[10px] font-semibold ${tone.text} ${tone.surface} ${tone.border}`}
        >
          {alarming ? '需要注意' : '正常'}
        </span>
      }
    >
      <p
        data-hgc-instrument-summary={section.id}
        className={`m-0 mb-2 rounded-control border-l-4 px-2 py-1 text-xs font-semibold ${tone.borderLeft} ${
          alarming ? `${tone.text} ${tone.surface}` : 'text-fg-muted'
        }`}
      >
        {section.summary}
      </p>

      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {section.items.map((item) => (
          <li
            key={item.id}
            data-hgc-instrument={item.id}
            data-hgc-instrument-freshness={item.freshness}
            className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5"
          >
            <span className="w-24 shrink-0 text-[10px] text-fg-subtle">{item.label}</span>
            <span
              data-hgc-instrument-value=""
              className={`font-mono text-sm tabular-nums ${STATUS_TONES[item.level].text}`}
            >
              {item.value}
            </span>
            {item.unit === '' ? null : <span className="text-[10px] text-fg-muted">{item.unit}</span>}
            <span className="min-w-0 flex-1 truncate text-right text-[10px] text-fg-subtle">{item.note}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** 仪表容器：把车辆状态与链路统计接进来，并按链路阶段解释"为什么这里是空的"。 */
export function InstrumentPanel(): JSX.Element {
  const live = useVehicleState();
  const stats = useLinkStats();
  const status = useLinkStatus();
  const now = useNow(1000);

  // 与 HUD 同一条规则：断开后 `LinkManager` 仍留着最后一帧，但界面不把它当实时值
  const connected = status.phase === 'connected';
  const state = connected ? live : null;

  const emptyReason =
    connected && live === null
      ? '链路已连接，但还没有收到任何机体的帧。仪表只显示真实收到的值：宁可空着，也不给一个看起来正常的假数。'
      : `链路未连接（${status.title}）。断开后不再显示上一帧的电压与卫星数 —— 它们只会越来越旧。`;

  return <Instruments state={state} stats={stats} now={now} emptyReason={emptyReason} />;
}
